'use client';

import type { ReactNode } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import { ChevronRight, Star } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { getReviewsEntryVariant } from './reviewsAvailability';
import { ReviewsSourceIcon } from './ReviewsSourceIcon';
import * as styles from './reviews.styles';

export type AggregateReviewsRating = {
  rating: number;
  reviewCount: number;
};

type ReviewsEntryPointProps = {
  hasReputation?: boolean;
  onEntryClick?: () => void;
  aggregateRating: AggregateReviewsRating | null;
  hasGooglePlace: boolean;
  hasRedditLinks: boolean;
  targetId: string;
};

const getAccessibleLabel = (
  { aggregateRating, hasGooglePlace, hasRedditLinks, hasReputation }: Omit<ReviewsEntryPointProps, 'targetId'>,
  t: ReturnType<typeof useTranslations<'hdp'>>,
  format: ReturnType<typeof useFormatter>,
): string => {
  const sourceLabels: Array<string> = [];
  if (aggregateRating) {
    sourceLabels.push(
      t('reviewsWebRating', {
        rating: format.number(aggregateRating.rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        count: aggregateRating.reviewCount,
      }),
    );
  }
  if (!aggregateRating && hasReputation) sourceLabels.push(t('reviewsWeb'));
  if (hasGooglePlace) sourceLabels.push(t('reviewsGoogle'));
  if (hasRedditLinks) sourceLabels.push(t('reviewsReddit'));

  const sources = format.list(sourceLabels, { type: 'conjunction' });
  return t('reviewsViewSources', { sources });
};

export function ReviewsEntryPoint({
  aggregateRating,
  hasGooglePlace,
  hasRedditLinks,
  targetId,
  hasReputation = aggregateRating !== null,
  onEntryClick,
}: ReviewsEntryPointProps) {
  const t = useTranslations('hdp');
  const format = useFormatter();
  const variant = getReviewsEntryVariant({
    hasReputation,
    hasGooglePlace,
    hasRedditLinks,
  });

  if (!variant) return null;

  const hasAggregate = aggregateRating !== null;
  const showsMultipleProviderTypes = hasAggregate && (hasGooglePlace || hasRedditLinks);
  const rating = aggregateRating
    ? format.number(aggregateRating.rating, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : undefined;
  const count = aggregateRating ? format.number(aggregateRating.reviewCount) : null;
  const emphasis = { strong: (chunks: ReactNode) => <strong>{chunks}</strong> };
  const descriptor = (() => {
    if (variant === 'web-google-reddit') return t('reviewsAndDiscussions');
    if (variant === 'web-google') return t('reviewsLabel');
    if (variant === 'web-reddit') return t('reviewsDiscussions');
    if (variant === 'web')
      return aggregateRating === null
        ? t.rich('reviewsAcrossWebEmpty', emphasis)
        : t('reviewsAcrossWeb', { count: aggregateRating.reviewCount });
    if (variant === 'google-reddit') return t.rich('reviewsBothAboutPlace', emphasis);
    if (variant === 'google') return t.rich('reviewsAboutPlace', emphasis);
    return t.rich('reviewsDiscussionsAboutPlace', emphasis);
  })();

  return (
    <a
      className={styles.entryPoint}
      href={`#${targetId}`}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        requestAnimationFrame(() => document.getElementById(`${targetId}-title`)?.focus({ preventScroll: true }));
        onEntryClick?.();
      }}
      aria-label={getAccessibleLabel({ aggregateRating, hasGooglePlace, hasRedditLinks, hasReputation }, t, format)}
    >
      <span className={styles.entryPointContent}>
        {hasAggregate && (
          <span className={styles.ratingGroup}>
            <LucideIcon icon={Star} size="sm" fill="currentColor" className={styles.ratingStar} />
            <span className={styles.ratingValue}>{rating}</span>
            {showsMultipleProviderTypes && <span className={styles.reviewCount}>({count})</span>}
          </span>
        )}

        {showsMultipleProviderTypes && <span className={styles.sourceDivider} aria-hidden="true" />}

        {(hasGooglePlace || hasRedditLinks) && (
          <span className={styles.sourceMarks} aria-hidden="true">
            {hasGooglePlace && <ReviewsSourceIcon source="google" />}
            {hasRedditLinks && <ReviewsSourceIcon source="reddit" />}
          </span>
        )}

        <span className={styles.entryPointLabel}>{descriptor}</span>
      </span>
      <LucideIcon icon={ChevronRight} size="sm" className={styles.entryPointChevron} />
    </a>
  );
}
